/// <amd-module name="Wit/SprintsHub/SprintsView/Components/Taskboard/SignalR/TaskboardSignalRService" />
import { IHubHostInformation } from "VSS/Features/SignalR/Hub";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssPageService } from "VSS/Platform/Page";
import { traceException } from "VSS/Platform/Trace";
import { WorkItemSignalREventType } from "Wit/SprintsHub/Common/Constants";
import {
    ITaskboardWorkItemChangedPayload,
    ITaskboardWorkItemColumnChangedPayload,
    TaskboardChangedDefaultCallback,
    TaskboardWorkItemChangedCallback,
    TaskboardWorkItemColumnChangedCallback
} from "Wit/SprintsHub/Common/Contracts/Interfaces";
import { ITaskboardSignalROptions, TaskboardSignalRHub } from "./TaskBoardSignalRHub";

export const ITaskboardSignalRServiceId = "ITaskboardSignalRService";

const workItemsUpdatedDebounceMs = 500;
const boardSettingsUpdatedDebounceMs = 1000;

export interface ITaskboardSignalRService extends IVssService {
    watch(
        teamId: string,
        onWorkItemsUpdated: TaskboardWorkItemChangedCallback,
        onTaskboardWorkItemColumnUpdated: TaskboardWorkItemColumnChangedCallback,
        onTaskboardColumnOptionsChanged: TaskboardChangedDefaultCallback,
        onTaskboardCardSettingsChanged: TaskboardChangedDefaultCallback,
        onTeamSettingsChanged: TaskboardChangedDefaultCallback
    ): Promise<void>;

    endWatch(teamId: string): Promise<void>;
}

class TaskboardSignalRService extends VssService implements ITaskboardSignalRService {
    private hub: TaskboardSignalRHub | null;
    private teamId: string;

    public _serviceEnd(pageContext: IVssPageContext): void {
        super._serviceEnd(pageContext);
        this.endWatch(this.teamId);
        this.hub?.stop();
        this.hub = null;
    }

    public async watch(
        teamId: string,
        onWorkItemsUpdated: TaskboardWorkItemChangedCallback,
        onTaskboardWorkItemColumnUpdated: TaskboardWorkItemColumnChangedCallback,
        onTaskboardColumnOptionsChanged: TaskboardChangedDefaultCallback,
        onTaskboardCardSettingsChanged: TaskboardChangedDefaultCallback,
        onTeamSettingsChanged: TaskboardChangedDefaultCallback
    ): Promise<void> {
        this._ensureHubInitialized();
        this.teamId = teamId;

        //#region Work Item Updated
        let workItemsTimeoutHandle: number | null = null;
        let aggregatedWorkItemUpdates: ITaskboardWorkItemChangedPayload[] = [];

        function aggregatedDebouncedOnWorkItemsUpdated(updatePayload: ITaskboardWorkItemChangedPayload[]) {
            for (const update of updatePayload) {
                const matchingItemIndex = aggregatedWorkItemUpdates.findIndex(u => u.id === update.id);
                if (matchingItemIndex === -1) {
                    // We do not have an update for this work item yet
                    aggregatedWorkItemUpdates.push(update);
                    continue;
                }

                if (update.revision > aggregatedWorkItemUpdates[matchingItemIndex].revision) {
                    let changeType = update.changeType;
                    if (changeType === WorkItemSignalREventType.ItemUpdated) {
                        // If the change type is ItemUpdated, keep the original change type as the item will
                        // be update to date at the time of creation.
                        changeType = aggregatedWorkItemUpdates[matchingItemIndex].changeType;
                    }
                    // This update is more recent than the one already in the update list; replace it
                    aggregatedWorkItemUpdates[matchingItemIndex] = { ...update, changeType: changeType };
                    continue;
                }
            }

            workItemsTimeoutHandle && clearTimeout(workItemsTimeoutHandle);
            workItemsTimeoutHandle = setTimeout(() => {
                onWorkItemsUpdated(aggregatedWorkItemUpdates);
                workItemsTimeoutHandle = null;
                aggregatedWorkItemUpdates = [];
            }, workItemsUpdatedDebounceMs);
        }
        //#endregion

        //#region Work item column updated
        let columnsTimeoutHandle: number | null = null;
        let aggregatedColumnUpdates: ITaskboardWorkItemColumnChangedPayload[] = [];

        function aggregatedDebouncedOnColumnsChanged(updatePayload: ITaskboardWorkItemColumnChangedPayload[]) {
            for (const update of updatePayload) {
                if (aggregatedColumnUpdates.some(u => u.workItemId === update.workItemId)) {
                    aggregatedColumnUpdates.push(update);
                    continue;
                }
            }

            columnsTimeoutHandle && clearTimeout(columnsTimeoutHandle);
            columnsTimeoutHandle = setTimeout(() => {
                onTaskboardWorkItemColumnUpdated(updatePayload);
                columnsTimeoutHandle = null;
            }, boardSettingsUpdatedDebounceMs);
        }
        //#endregion

        //#region Column options updated
        let columnOptionsTimeoutHandle: number | null = null;
        function debouncedOnColumnOptionsChanged() {
            columnOptionsTimeoutHandle && clearTimeout(columnOptionsTimeoutHandle);
            columnOptionsTimeoutHandle = setTimeout(() => {
                onTaskboardColumnOptionsChanged();
                columnOptionsTimeoutHandle = null;
            }, boardSettingsUpdatedDebounceMs);
        }
        //#endregion

        //#region Card settings updated
        let cardSettingsTimeoutHandle: number | null = null;
        function debouncedOnCardSettingsChanged() {
            cardSettingsTimeoutHandle && clearTimeout(cardSettingsTimeoutHandle);
            cardSettingsTimeoutHandle = setTimeout(() => {
                onTaskboardCardSettingsChanged();
                cardSettingsTimeoutHandle = null;
            }, boardSettingsUpdatedDebounceMs);
        }
        //#endregion

        //#region Team settings updated
        let teamSettingsTimeoutHandle: number | null = null;
        function debouncedOnTeamSettingsChanged() {
            teamSettingsTimeoutHandle && clearTimeout(teamSettingsTimeoutHandle);
            teamSettingsTimeoutHandle = setTimeout(() => {
                onTeamSettingsChanged();
                teamSettingsTimeoutHandle = null;
            }, boardSettingsUpdatedDebounceMs);
        }
        //#endregion

        try {
            await this.hub!.watch(
                teamId,
                aggregatedDebouncedOnWorkItemsUpdated,
                aggregatedDebouncedOnColumnsChanged,
                debouncedOnColumnOptionsChanged,
                debouncedOnCardSettingsChanged,
                debouncedOnTeamSettingsChanged
            );
        } catch (error) {
            traceException(this.pageContext, "WIT", "WorkItem", error, { ["method"]: "SignalR.Watch" });
        }
    }

    public async endWatch(boardId: string): Promise<void> {
        try {
            await this.hub?.endWatch(boardId);
        } catch (error) {
            traceException(this.pageContext, "WIT", "WorkItem", error, { ["method"]: "SignalR.EndWatch" });
        }
    }

    private _ensureHubInitialized() {
        if (this.hub) {
            return;
        }

        const vssPageService = this.pageContext.getService<IVssPageService>("IVssPageService");
        const vssPageData = vssPageService.getData();
        if (!vssPageData) {
            throw new Error("No page data available");
        }

        const historyService = this.pageContext.getService<IVssHistoryService>("IVssHistoryService");

        const hostInformation: IHubHostInformation = {
            hostId: vssPageData.hostId,
            hostPath: vssPageData.hostPath,
            isHosted: vssPageData.isHosted
        };
        const options: ITaskboardSignalROptions = {
            hostInformation: hostInformation,
            historyService: historyService,
            pageContext: this.pageContext,
            useSignalRAppPoolDefaultValue: true
        };
        this.hub = new TaskboardSignalRHub(options);
    }
}

Services.add(ITaskboardSignalRServiceId, { serviceFactory: TaskboardSignalRService });
